// Material
import { List } from '@mui/material'
import { styled } from '@mui/material/styles'
// Common
import { ProfileActionItem } from '$common/profile-action-item'

import { AddUserIcon, GroupIcon } from '$common/icons'
import { LinkIcon } from '$common/icons/link'
import { SharedMediaProfileActionItem } from '$common/shared-media-profile-action-item'
// logic
import { useHall } from '$logic/hall/react-hook'
import { useRoom, useRoomAttribute } from '$logic/room/react-hook'
import {
  openAddHallMembers,
  openJoinHallRequests,
  openHallInviteLinks,
  openHallSubscribers,
} from '$logic/router'
import { MemberPermissions } from '$logic/types/subscription'
import { toLocale } from '$func/text'
// Localization
import { t } from '$i18n'
import { HallAdminsActionItem } from '$common/hall-admin-action-item'
import { useJoinHallRequests } from '$logic/subscription/react-hook'
import { JOIN_HALL_REQUEST_ENABLED } from '$logic/subscription/join-requests'
import { isHall } from '$logic/room'

interface Props {
  hallId: string
  isGroup: boolean
  permissions: MemberPermissions
}

const StyledList = styled(List)`
  padding-top: 0;
  padding-bottom: 0;
  background-color: white;
`

export function HallProfileActions({ hallId, isGroup, permissions }: Props) {
  const { entity: hall, loading: hallLoading } = useHall(hallId)

  const roomType = useRoomAttribute('type', hallId)

  const { totalCount: joinRequestsCount } = useJoinHallRequests({ hallId })

  return (
    <StyledList>
      {permissions?.inviteViaLink && roomType === 'CHANNEL' && (
        <ProfileActionItem
          Icon={<LinkIcon />}
          onClick={() => openHallInviteLinks(hallId)}
        >
          {t('profile.hall.actions.inviteViaLink')}
        </ProfileActionItem>
      )}

      {JOIN_HALL_REQUEST_ENABLED && isHall(roomType) && permissions.isAdmin && (
        <ProfileActionItem
          Icon={<AddUserIcon />}
          onClick={() => openJoinHallRequests(hallId)}
          secondaryText={
            joinRequestsCount ? toLocale(joinRequestsCount) : undefined
          }
        >
          {t('profile.hall.actions.joinRequests')}
        </ProfileActionItem>
      )}

      {permissions?.addMembers && (
        <ProfileActionItem
          Icon={<AddUserIcon />}
          onClick={() => openAddHallMembers(hallId, false)}
        >
          {roomType === 'GROUP'
            ? t('profile.hall.actions.addMember')
            : t('profile.hall.actions.addSubscriber')}
        </ProfileActionItem>
      )}

      {(permissions?.isAdmin || isGroup) && (
        <ProfileActionItem
          Icon={<GroupIcon />}
          onClick={() => openHallSubscribers(hallId)}
          secondaryText={toLocale(hall?.membersCount)}
          secondaryTextLoading={hallLoading}
        >
          {roomType === 'GROUP'
            ? t('profile.hall.actions.members')
            : t('profile.hall.actions.subscribers')}
        </ProfileActionItem>
      )}

      {permissions?.isAdmin && roomType === 'CHANNEL' && (
        <HallAdminsActionItem hallId={hallId} />
      )}

      <SharedMediaProfileActionItem roomId={hallId} />
    </StyledList>
  )
}
